(ns app.openrouter.image-pricing
  "Shared estimates for one generated image, optionally with one input image.
   Only documented flat rates are estimated. Token counts, pixel counts,
   quality mappings and the provider chosen by routing are never guessed:
   such settings get no estimate and are billed by actual usage."
  (:require [clojure.string :as str]))

(defn default-options
  [model]
  {:aspect-ratio (or (some #{"9:16"} (:model/aspect-ratios model))
                     (first (:model/aspect-ratios model)))
   :resolution (first (:model/resolutions model))})

(defn option-error
  "Validate the selections actually sent to the provider, without filling
   missing values or accepting options the model does not advertise."
  [model options]
  (some (fn [[field model-field label]]
          (let [value (get options field)
                supported (get model model-field)]
            (cond
              (and (seq supported) (nil? value)) (str label " is required")
              (and value (not (some #{value} supported)))
              (str label " is not supported by this model"))))
        [[:aspect-ratio :model/aspect-ratios "Aspect ratio"]
         [:resolution :model/resolutions "Resolution"]]))

(defn- valid-cost?
  [cost]
  (and (number? cost)
       #?(:clj (Double/isFinite (double cost)) :cljs (js/isFinite cost))
       (not (neg? cost))))

(def ^:private resolution-rank
  {"512" 0 "1k" 1 "2k" 2 "4k" 3})

(defn- below-variants?
  "Riverflow lists an unqualified rate next to 2k and 4k rows, so the
   unqualified rate prices the resolutions below the smallest variant.
   Above that it would underestimate (4K next to a 2k row)."
  [variants resolution]
  (let [rank (resolution-rank resolution)
        ranks (map #(resolution-rank (str/lower-case %)) variants)]
    (boolean (and rank
                  (every? some? ranks)
                  (< rank (apply min ranks))))))

(defn- selected-rate
  "Resolution variants must explicitly match a catalog resolution. When a
   group has variants, its unqualified row only prices the resolutions below
   the smallest variant. Unknown variants (e.g. high_resolution or low_1k)
   are refused."
  [rates model options]
  (let [variants (keep :variant rates)
        resolutions (set (map str/lower-case (:model/resolutions model)))
        resolution (some-> (:resolution options) str/lower-case)
        known? (every? #(and (string? %) (contains? resolutions (str/lower-case %))) variants)
        variant-rows (filter #(and (:variant %)
                                   (= resolution (str/lower-case (:variant %)))) rates)
        matching (cond
                   (empty? variants) rates
                   (not known?) []
                   (seq variant-rows) variant-rows
                   (below-variants? variants resolution) (remove :variant rates)
                   :else [])
        candidates (distinct (map #(select-keys % [:unit :cost_usd]) matching))]
    (when (= 1 (count candidates))
      (first candidates))))

(defn- meter-cost
  [billable rates model category options]
  (let [quantity (case billable
                   ("output_image" "input_text") 1
                   ("input_image" "input_reference") (if (= :image-editing category) 1 0)
                   ;; Neither image command accepts fonts.
                   "input_font" 0
                   nil)]
    (when quantity
      (if (zero? quantity)
        0.0
        (let [{:keys [unit cost_usd]} (selected-rate rates model options)]
          (when (and (valid-cost? cost_usd)
                     (or (= unit "request")
                         (and (= unit "image") (not= billable "input_text"))))
            (* quantity cost_usd)))))))

(defn- endpoint-cost
  [rates model category options]
  (let [groups (group-by :billable rates)
        costs (map (fn [[billable entries]]
                     (meter-cost billable entries model category options))
                   groups)]
    (when (and (seq (get groups "output_image"))
               (every? some? costs))
      (reduce + 0.0 costs))))

(defn estimate
  "Customer USD including the combined markup, or nil if pricing is unknown.
   All advertised endpoints must be priceable; use the highest total rather
   than assuming routing selects the cheapest provider. Editing includes the
   input-image/reference charge. Actual billing still uses reported usage."
  [model category options]
  (when (and (#{:image-generation :image-editing} category)
             (nil? (option-error model options)))
    (let [{:keys [markup endpoints]} (:model/image-pricing model)
          costs (map #(endpoint-cost (:pricing %) model category options) endpoints)]
      (when (and (valid-cost? markup) (pos? markup)
                 (seq costs) (every? some? costs))
        ;; Match the usage-cost precision and avoid rejecting a balance of
        ;; $0.052 because binary multiplication produced 0.052000000000000005.
        (let [scaled (* 1e8 markup (apply max costs))]
          (when (valid-cost? scaled)
            (/ (#?(:clj Math/floor :cljs js/Math.floor) (+ scaled 0.5))
               1e8)))))))

(defn option-hints
  [{:keys [resolution aspect-ratio]}]
  (str/join " " (remove nil? [resolution aspect-ratio])))

(defn format-usd
  [amount]
  (let [digits (if (< amount 0.01) 3 2)]
    (str "$" #?(:clj (String/format java.util.Locale/US
                                    (str "%." digits "f")
                                    (to-array [(double amount)]))
                :cljs (.toFixed amount digits)))))

(defn price-label
  [model category options]
  (let [price (estimate model category options)
        hints (option-hints options)]
    (str (if price (str "~" (format-usd price) " / image") "Billed by usage")
         (when (seq hints) (str " (" hints ")")))))
